import { useState, useCallback, useMemo, useRef } from 'react'
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceArea
} from 'recharts'

function ProductionChart({ actualData = [], forecastData = [], viewMode = 'gross' }) {
    const [refAreaLeft, setRefAreaLeft] = useState(null)
    const [refAreaRight, setRefAreaRight] = useState(null)
    const [zoomDomain, setZoomDomain] = useState(null) // { left, right }
    const isDragging = useRef(false)

    if (actualData.length === 0 && forecastData.length === 0) {
        return (
            <div className="h-80 flex items-center justify-center text-gray-400">
                No production data available
            </div>
        )
    }

    const isNet = viewMode === 'net'
    const dataByDate = new Map()

    actualData.forEach(item => {
        dataByDate.set(item.date, {
            date: item.date,
            actual_oil: isNet ? (item.net_oil_bbl ?? 0) : item.oil_bbl,
            actual_gas: isNet ? (item.net_gas_mcf ?? 0) : item.gas_mcf,
            actual_water: item.water_bbl,
            forecast_oil: null,
        })
    })

    forecastData.forEach(item => {
        const forecastOil = isNet ? (item.net_oil_bbl ?? 0) : item.oil_bbl
        const existing = dataByDate.get(item.date)
        if (existing) {
            existing.forecast_oil = forecastOil
        } else {
            dataByDate.set(item.date, {
                date: item.date,
                actual_oil: null,
                actual_gas: null,
                actual_water: null,
                forecast_oil: forecastOil,
            })
        }
    })

    const allData = Array.from(dataByDate.values())
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .map(item => ({ ...item, rawDate: item.date }))

    // Apply zoom filter
    const formattedData = zoomDomain
        ? allData.filter(d => d.rawDate >= zoomDomain.left && d.rawDate <= zoomDomain.right)
        : allData

    const monthTicks = formattedData
        .filter(item => new Date(item.date).getDate() === 1)
        .map(item => item.date)

    // Compute Y-axis domains for zoomed data so the chart rescales vertically
    const yLeftValues = formattedData.flatMap(d => [d.actual_oil, d.actual_gas, d.forecast_oil].filter(v => v != null && v > 0))
    const yRightValues = formattedData.flatMap(d => [d.actual_water].filter(v => v != null && v > 0))
    const yLeftDomain = yLeftValues.length > 0 ? [0, Math.ceil(Math.max(...yLeftValues) * 1.1)] : [0, 'auto']
    const yRightDomain = yRightValues.length > 0 ? [0, Math.ceil(Math.max(...yRightValues) * 1.1)] : [0, 'auto']

    const label = isNet ? 'Net' : 'Gross'

    // --- Zoom handlers ---
    const handleMouseDown = (e) => {
        if (e?.activeLabel) {
            setRefAreaLeft(e.activeLabel)
            setRefAreaRight(null)
            isDragging.current = true
        }
    }

    const handleMouseMove = (e) => {
        if (isDragging.current && e?.activeLabel) {
            setRefAreaRight(e.activeLabel)
        }
    }

    const handleMouseUp = () => {
        isDragging.current = false
        if (refAreaLeft && refAreaRight && refAreaLeft !== refAreaRight) {
            // Ensure left < right
            const [left, right] = [refAreaLeft, refAreaRight].sort()
            setZoomDomain({ left, right })
        }
        setRefAreaLeft(null)
        setRefAreaRight(null)
    }

    const handleResetZoom = () => {
        setZoomDomain(null)
        setRefAreaLeft(null)
        setRefAreaRight(null)
    }

    return (
        <div>
            {/* Zoom controls */}
            <div className="flex items-center justify-between mb-2 min-h-[28px]">
                <p className="text-xs text-slate-500 select-none">
                    {zoomDomain ? 'Pinch or drag to zoom further' : 'Click & drag on chart to zoom in'}
                </p>
                {zoomDomain && (
                    <button
                        onClick={handleResetZoom}
                        className="text-xs px-2.5 py-1 rounded bg-slate-800 text-blue-400 hover:bg-slate-700 border border-slate-700 transition flex items-center gap-1"
                    >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                        </svg>
                        Reset Zoom
                    </button>
                )}
            </div>

            <div className="h-80 select-none touch-none">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={formattedData}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onTouchStart={(e) => { if (e?.activeLabel) handleMouseDown(e) }}
                        onTouchMove={(e) => { if (e?.activeLabel) handleMouseMove(e) }}
                        onTouchEnd={handleMouseUp}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis
                            dataKey="rawDate"
                            ticks={monthTicks.length > 0 ? monthTicks : undefined}
                            tick={{ fontSize: 12, fill: '#94a3b8' }}
                            tickLine={{ stroke: '#475569' }}
                            stroke="#475569"
                            tickFormatter={(date) => {
                                const d = new Date(date)
                                // Show day-level ticks when zoomed to < 60 days
                                if (zoomDomain) {
                                    const span = (new Date(zoomDomain.right) - new Date(zoomDomain.left)) / (1000 * 60 * 60 * 24)
                                    if (span <= 14) return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                    if (span <= 60) return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                }
                                return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                            }}
                            interval={zoomDomain ? 'preserveStartEnd' : undefined}
                        />
                        <YAxis
                            yAxisId="left"
                            domain={yLeftDomain}
                            tick={{ fontSize: 12, fill: '#94a3b8' }}
                            tickLine={{ stroke: '#475569' }}
                            stroke="#475569"
                            tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}
                            label={{ value: `${label} Oil (BBL) / Gas (MCF)`, angle: -90, position: 'insideLeft', dx: -10, style: { fontSize: 11, fill: '#cbd5e1' } }}
                        />
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            domain={yRightDomain}
                            tick={{ fontSize: 12, fill: '#94a3b8' }}
                            tickLine={{ stroke: '#475569' }}
                            stroke="#475569"
                            tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                            label={{ value: 'Water (BBL)', angle: 90, position: 'insideRight', dx: 10, style: { fontSize: 11, fill: '#cbd5e1' } }}
                        />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: '#1e293b',
                                border: '1px solid #475569',
                                borderRadius: '8px',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
                                color: '#e2e8f0'
                            }}
                            formatter={(value) => value?.toLocaleString() || '--'}
                        />
                        <Legend wrapperStyle={{ color: '#cbd5e1' }} />
                        <Line yAxisId="left" type="monotone" dataKey="actual_oil" stroke="#22c55e" strokeWidth={3} dot={false} activeDot={{ r: 6 }} name={`${label} Oil (BBL)`} connectNulls={false} />
                        <Line yAxisId="left" type="monotone" dataKey="actual_gas" stroke="#ef4444" strokeWidth={3} dot={false} activeDot={{ r: 6 }} name={`${label} Gas (MCF)`} connectNulls={false} />
                        <Line yAxisId="right" type="monotone" dataKey="actual_water" stroke="#3b82f6" strokeWidth={3} dot={false} activeDot={{ r: 6 }} name="Water (BBL)" connectNulls={false} />
                        <Line yAxisId="left" type="monotone" dataKey="forecast_oil" stroke="#10b981" strokeWidth={2.5} strokeDasharray="8 4" dot={false} activeDot={{ r: 5 }} name={`${label} Oil Forecast`} connectNulls={true} />

                        {/* Drag selection highlight */}
                        {refAreaLeft && refAreaRight && (
                            <ReferenceArea
                                yAxisId="left"
                                x1={refAreaLeft}
                                x2={refAreaRight}
                                strokeOpacity={0.3}
                                fill="#3b82f6"
                                fillOpacity={0.15}
                            />
                        )}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    )
}

export default ProductionChart
